import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, FileSignature, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePaywall } from "@/hooks/usePaywall";

function TemplatePicker({ kind, onPick, testid }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/templates", { params: { kind } }).then(r => setItems(r.data)); }, [kind]);
  if (!items.length) return null;
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(v => !v)} data-testid={testid}
        className="text-xs text-[#0f4c5c] hover:underline inline-flex items-center gap-1">
        <Sparkles className="w-3 h-3"/> Usar modelo ({items.length})
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-lg z-10 max-h-64 overflow-auto">
          {items.map(t => (
            <button key={t.id} type="button" onClick={() => { onPick(t.content); setOpen(false); }}
              className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-50 last:border-0">
              <div className="text-sm font-medium text-slate-900 truncate">{t.title}</div>
              <div className="text-xs text-slate-500 truncate mt-0.5">{t.content}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ContractForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [objeto, setObjeto] = useState("");
  const [valor, setValor] = useState("");
  const [forma, setForma] = useState("");
  const [loading, setLoading] = useState(false);
  const { authorize, PaywallElement } = usePaywall("PECA_GERACAO");

  const submit = async () => {
    if (!objeto.trim() || !valor.trim() || !forma.trim()) {
      toast.error("Preencha objeto, valor e forma de pagamento."); return;
    }
    // Paywall gate — geração de peça é always_paid (R$ 5,00 por operação)
    const authorization = await authorize({ quantity: 1 });
    if (!authorization) { toast.info("Geração cancelada."); return; }
    setLoading(true);
    try {
      const r = await api.post("/documents/generate", {
        type: "contrato_honorarios", client_id: id,
        contract: { objeto, valor, forma_pagamento: forma },
      });
      await authorization.commit();
      navigate(`/documents/${r.data.id}`);
    } catch (e) {
      await authorization.release();
      toast.error("Erro ao gerar contrato");
    }
    finally { setLoading(false); }
  };

  return (
    <div className="flex-1 min-w-0 flex flex-col p-6 lg:p-8 font-sans max-w-3xl mx-auto w-full">
      <button onClick={() => navigate(`/clients/${id}/new-document`)} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-4">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>
      <h1 className="font-heading text-3xl font-semibold text-slate-900">Contrato de Honorários</h1>
      <p className="text-slate-500 mt-1">Os textos abaixo serão inseridos <b>exatamente como digitados</b>, sem reescrita.</p>

      <div className="mt-8 bg-white rounded-2xl border border-slate-100 p-6 space-y-5">
        <label className="block">
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs font-medium text-slate-600">Objeto do contrato</div>
            <TemplatePicker kind="objeto" onPick={setObjeto} testid="pick-objeto"/>
          </div>
          <textarea data-testid="contract-objeto" value={objeto} onChange={(e) => setObjeto(e.target.value)} rows={4}
            placeholder="Ex.: Propositura e acompanhamento de Mandado de Segurança visando à matrícula..."
            className="w-full p-3 rounded-lg bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-slate-900 outline-none text-sm"/>
        </label>
        <label className="block">
          <div className="text-xs font-medium text-slate-600 mb-1">Valor dos honorários</div>
          <input data-testid="contract-valor" value={valor} onChange={(e) => setValor(e.target.value)}
            placeholder="Ex.: R$ 5.000,00"
            className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-slate-900 outline-none text-sm"/>
        </label>
        <label className="block">
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs font-medium text-slate-600">Forma de pagamento</div>
            <TemplatePicker kind="forma_pagamento" onPick={setForma} testid="pick-forma"/>
          </div>
          <textarea data-testid="contract-forma" value={forma} onChange={(e) => setForma(e.target.value)} rows={3}
            placeholder="Ex.: R$ 2.000,00 no ato da assinatura e 3 parcelas mensais de R$ 1.000,00."
            className="w-full p-3 rounded-lg bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-slate-900 outline-none text-sm"/>
        </label>
      </div>

      <button data-testid="btn-generate-contract" onClick={submit} disabled={loading}
        className="mt-6 w-full gradient-primary text-white h-14 rounded-xl text-base font-medium inline-flex items-center justify-center gap-2 hover:-translate-y-0.5 transition">
        <FileSignature className="w-5 h-5" /> {loading ? "Gerando..." : "OK, GERAR CONTRATO"}
      </button>
      {PaywallElement}
    </div>
  );
}
